// src/Views/AuthAction.js
import React, { useEffect, useState, useRef } from 'react'; // Added useRef
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getAuth, applyActionCode, confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { Icon } from '@iconify/react';
import './AuthAction.css';

const AuthAction = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    
    // Locks to prevent double-execution
    const effectRan = useRef(false);

    // State
    const [status, setStatus] = useState('loading'); 
    const [newPassword, setNewPassword] = useState('');
    const [message, setMessage] = useState('Initializing security protocol...');

    const mode = searchParams.get('mode'); 
    const oobCode = searchParams.get('oobCode');

    useEffect(() => {
        // 1. If no code, stop immediately.
        if (!oobCode) {
            setStatus('error');
            setMessage('Error: No security code found in URL.');
            return;
        }

        // 2. Prevent React from running this twice (Strict Mode fix)
        if (effectRan.current === true) return;
        effectRan.current = true;

        const auth = getAuth();

        // 3. ROUTING LOGIC
        if (mode === 'verifyEmail') {
            setMessage('Verifying email address...');
            applyActionCode(auth, oobCode)
                .then(() => {
                    setStatus('success');
                    setMessage('Email verified! Access granted.');
                })
                .catch((error) => {
                    console.error("Verification Error:", error);
                    setStatus('error');
                    setMessage("This link has expired or was already used.");
                });
        } 
        else if (mode === 'resetPassword') {
            setMessage('Validating reset token...');
            verifyPasswordResetCode(auth, oobCode)
                .then((email) => {
                    setStatus('input'); 
                    setMessage(`Resetting password for ${email}`);
                })
                .catch((error) => {
                    console.error("Reset Error:", error);
                    setStatus('error');
                    setMessage('This password reset link is invalid or expired.');
                });
        } 
        else {
            setStatus('error');
            setMessage('Unknown security mode.');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Empty dependency array ensures it only mounts once

    const handlePasswordSubmit = (e) => {
        e.preventDefault();
        const auth = getAuth();
        
        if (newPassword.length < 6) {
            alert("Password is too short (min 6 chars)");
            return;
        }
        
        setStatus('loading');
        confirmPasswordReset(auth, oobCode, newPassword)
            .then(() => {
                setStatus('success');
                setMessage('Password reset complete. Logging you out...');
                setTimeout(() => navigate('/signin'), 3000);
            })
            .catch((error) => {
                setStatus('error');
                setMessage(error.message);
            });
    };

    return (
        <div className="auth-action-container">
            <div className="auth-card">
                <div className="auth-icon-wrap">
                    {status === 'success' ? <Icon icon="solar:check-circle-bold" color="#10b981" width="40" /> :
                     status === 'error' ? <Icon icon="solar:danger-circle-bold" color="#ef4444" width="40" /> :
                     <Icon icon="solar:shield-keyhole-linear" color="#3b82f6" width="40" />}
                </div>

                <h2>
                    {mode === 'resetPassword' ? 'Secure Reset' : 
                     mode === 'verifyEmail' ? 'Email Verification' : 'Security Check'}
                </h2>

                <p className="auth-message">{message}</p>

                {status === 'input' && mode === 'resetPassword' && (
                    <form onSubmit={handlePasswordSubmit} className="auth-form">
                        <input 
                            type="password" 
                            placeholder="Enter new password" 
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="auth-input"
                            autoFocus
                        />
                        <button type="submit" className="auth-btn">Update Password</button>
                    </form>
                )}

                {(status === 'success' || status === 'error') && (
                    <button onClick={() => navigate('/signin')} className="auth-btn auth-btn-ghost" style={{ marginTop: '1rem' }}>
                        Return to Login
                    </button>
                )}
            </div>
        </div>
    );
};

export default AuthAction;